import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { useToast } from '../components/layout/ToastProvider';

const { FiUser, FiBell, FiShield, FiCreditCard, FiGlobe, FiDatabase, FiLogOut } = FiIcons;

export default function Settings() {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [profile, setProfile] = useState({
    full_name: '',
    business_name: '',
    bio: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      if (data) setProfile(data);
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setFetching(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          ...profile,
          updated_at: new Date()
        });

      if (error) throw error;
      addToast("Profile updated successfully!");
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  const tabs = [
    { icon: FiUser, label: 'Profile' },
    { icon: FiGlobe, label: 'Branding' },
    { icon: FiBell, label: 'Notifications' },
    { icon: FiCreditCard, label: 'Billing' },
    { icon: FiShield, label: 'Security' },
    { icon: FiDatabase, label: 'Integrations' },
  ];

  if (fetching) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div></div>;

  return (
    <div className="max-w-4xl space-y-8">
      <header className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Settings</h1>
          <p className="text-slate-400 mt-1">Manage your account and platform configuration.</p>
        </div>
        <button 
          onClick={handleSignOut}
          className="bg-rose-500/10 text-rose-400 hover:bg-rose-500 hover:text-white px-4 py-2 rounded-xl flex items-center gap-2 font-bold transition-all"
        >
          <SafeIcon icon={FiLogOut} /> Sign Out
        </button>
      </header>

      <div className="flex gap-8">
        <aside className="w-48 shrink-0 space-y-1">
          {tabs.map((tab) => (
            <button key={tab.label} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${tab.label === 'Profile' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-900'}`}>
              <SafeIcon icon={tab.icon} /> {tab.label}
            </button>
          ))}
        </aside>

        <div className="flex-1 space-y-8">
          <form onSubmit={handleUpdate} className="bg-slate-900 border border-slate-800 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-white mb-6">Personal Information</h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-400">Full Name</label>
                <input 
                  type="text" 
                  value={profile.full_name}
                  onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                  className="w-full bg-slate-800 border-none rounded-xl px-4 py-2.5 text-white outline-none focus:ring-2 focus:ring-indigo-500" 
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-400">Email Address (Read Only)</label>
                <input type="email" disabled className="w-full bg-slate-800/50 border-none rounded-xl px-4 py-2.5 text-slate-500 outline-none cursor-not-allowed" placeholder="john@mycoaching.com" />
              </div>
              <div className="col-span-2 space-y-2">
                <label className="text-sm font-medium text-slate-400">Business Name</label>
                <input 
                  type="text" 
                  value={profile.business_name}
                  onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
                  className="w-full bg-slate-800 border-none rounded-xl px-4 py-2.5 text-white outline-none focus:ring-2 focus:ring-indigo-500" 
                />
              </div>
              <div className="col-span-2 space-y-2">
                <label className="text-sm font-medium text-slate-400">Bio</label>
                <textarea 
                  rows={4} 
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  className="w-full bg-slate-800 border-none rounded-xl px-4 py-2.5 text-white outline-none focus:ring-2 focus:ring-indigo-500 resize-none" 
                />
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-slate-800 flex justify-end gap-4">
              <button type="button" onClick={fetchProfile} className="text-slate-400 hover:text-white px-6 py-2.5 rounded-xl font-medium transition-colors">Discard</button>
              <button disabled={loading} type="submit" className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl font-bold transition-all">
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-white mb-6">Stripe Integration</h3>
            <div className="flex items-center justify-between p-4 bg-slate-800 rounded-2xl">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center text-white text-2xl font-bold">S</div>
                <div>
                  <p className="text-white font-bold">Stripe Connected</p>
                  <p className="text-xs text-slate-500">Live mode active • acct_1N23...987</p>
                </div>
              </div>
              <button className="text-rose-400 hover:text-rose-300 text-sm font-bold">Disconnect</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}